import React, { useState } from 'react';
import { Alert, Button, Checkbox, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { IconPhone } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { HealthcareProfessional } from '../../../global/entities/healthcareProfessional/HealthcareProfessional.ts';
import Utils from '../../../global/helpers/utils.ts';
import { useReenrollHcp } from '../../../global/entities/healthcareProfessional/mutations/useReenrollHcp.tsx';
import { mobileErrorKeyOf } from '../../../global/helpers/mobileErrors.ts';

type ReenrollHcpModalProps = {
    professional: HealthcareProfessional;
    onClose: () => void;
};

export const ReenrollHcpModal: React.FC<ReenrollHcpModalProps> = ({ professional, onClose }) => {
    const { t } = useTranslation();
    const [mobile, setMobile] = useState('');
    const [identityConfirmed, setIdentityConfirmed] = useState(false);
    const reenroll = useReenrollHcp();
    const canSubmit = mobile.trim() !== '' && identityConfirmed;
    const mobileErrorKey = mobileErrorKeyOf(reenroll.error);
    const otherError = reenroll.isError && !mobileErrorKey;

    const handleSubmit = async () => {
        try {
            await reenroll.mutateAsync({ healthcareProfessionalId: professional.id, reenrollment: { mobile, identityConfirmed } });
            onClose();
        } catch {
            // the field shows the refused mobile from the mutation state
        }
    };

    return (
        <Modal opened onClose={onClose} title={t('hcp.reenroll')}>
            <Stack gap="sm">
                <Text size="sm">{professional.name}</Text>
                <TextInput
                    label={t('hcp.reenrollMobile')}
                    placeholder="(415) 555-1212"
                    leftSection={<IconPhone size={16} />}
                    value={mobile}
                    onChange={(e) => setMobile(e.currentTarget.value)}
                    onBlur={(e) => setMobile(Utils.formatPhoneNumber(e.currentTarget.value))}
                    error={mobileErrorKey ? t(mobileErrorKey) : undefined}
                />
                <Checkbox
                    label={t('hcp.identityConfirmed')}
                    checked={identityConfirmed}
                    onChange={(e) => setIdentityConfirmed(e.currentTarget.checked)}
                />
                {otherError && (
                    <Alert color="red" role="alert">
                        {t('hcp.reenrollFailed')}
                    </Alert>
                )}
                <Group justify="flex-end">
                    <Button type="button" variant="default" onClick={onClose}>
                        {t('button.cancel')}
                    </Button>
                    <Button type="button" disabled={!canSubmit} loading={reenroll.isPending} onClick={handleSubmit}>
                        {t('hcp.reenrollConfirm')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
